<template>
    <v-container>
        <v-container v-if="!isLoading">
            <div id="main">
                <div id="inner">
                    <vue-pdf-embed :source="pdfUrl" v-if="pdfUrl" />
                </div>
            </div>
        </v-container>
        <v-skeleton-loader type="card" v-else />
        <v-form ref="form" @submit.prevent="save">
            <v-container v-if="!isLoading && signers">
                <v-row>
                    <v-col>
                        <v-select
                            :rules="[validationRules.required]"
                            item-text="name"
                            item-value="record_id"
                            v-model="selectedSigner"
                            v-if="!isLoading"
                            outlined
                            color="gray"
                            :items="signers"
                            label="Выбрать подписанта"
                        />
                        <v-skeleton-loader v-else type="text" />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>Способ подписания:</v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-radio-group row v-model="selectedSignMethod">
                            <v-radio
                                color="gray"
                                v-for="signMethod in availableSignMethods"
                                :key="signMethod.value"
                                :label="signMethod.label"
                                :value="signMethod.value"
                            ></v-radio>
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <StylusSign ref="StylusSign" v-if="selectedSignMethod === 'stylus'" />
                        <EDSSign :document="base64document" v-if="selectedSignMethod === 'eds'" @getEDSData="getEDSData" />
                        <QRCodeSign
                            v-if="selectedSignMethod === 'qrCode'"
                            :document="base64document"
                            @getQRData="getQRData"
                            :id="transferData.record_id"
                            :file_name="'Акт на отключение подключение'"
                        />
                    </v-col>
                </v-row>
            </v-container>
            <v-row v-if="!isLoading">
                <v-spacer />
                <v-col cols="2" v-if="!this.only_sign">
                    <v-btn @click="goToPreviousStep" color="gray" outlined rounded>Назад</v-btn>
                </v-col>
                <v-col cols="2">
                    <v-btn v-if="signers.length" color="gray" outlined rounded @click="cancel">Отмена</v-btn>
                    <v-btn v-else color="gray" outlined rounded @click="cancel">Закрыть</v-btn>
                </v-col>
                <v-col v-if="signers.length === 2">
                    <v-btn type="submit" color="gray" class="white--text" rounded>Сохранить и продолжить</v-btn>
                </v-col>
                <v-col v-if="signers.length === 1">
                    <v-btn type="submit" color="gray" class="white--text" rounded>Сохранить</v-btn>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>
<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import { base64ToObjectURL } from '@/utils';
import { validationRules } from '@/utils/validationRules';
import translate from '@/utils/translate.util';
import StylusSign from '@/components/sign_document/StylusSign.vue';
import QRCodeSign from '@/components/sign_document/QRCodeSign.vue';
import EDSSign from '@/components/sign_document/EDSSign.vue';

const SignTypes = {
    STYLUS: 'stylus',
    EDS: 'eds',
    QRCode: 'qrCode'
};

export default {
    name: 'SignAct',
    components: { EDSSign, QRCodeSign, StylusSign, VuePdfEmbed },
    props: ['transferData', 'only_sign'],
    inject: ['getActPdf', 'getActSignInfo', 'postActSign'],
    data: function() {
        return {
            isLoading: false,
            pdfUrl: null,
            signers: [],
            selectedSigner: null,
            signMethods: [
                { label: 'Стилус', value: SignTypes.STYLUS, show: false },
                { label: 'С помощью ЭЦП', value: SignTypes.EDS, show: false },
                { label: 'QR code', value: SignTypes.QRCode, show: false }
            ],
            selectedSignMethod: SignTypes.STYLUS,
            signData: null,
            base64document: null
        };
    },
    computed: {
        validationRules() {
            return validationRules;
        },
        signatory() {
            return this.selectedSigner === 1 ? 'engineer_sign' : 'consumer_sign';
        },
        availableSignMethods() {
            return this.signMethods.filter(signMethod => signMethod.show);
        }
    },
    methods: {
        goToPreviousStep() {
            this.$emit('goToPreviousStep');
        },
        cancel() {
            this.$emit('cancel');
        },
        getStylusData() {
            const dataStylus = this.$refs.StylusSign.$refs.signaturePad.saveSignature();
            this.signData = dataStylus.data;
        },
        getQRData(qrData) {
            this.signData = qrData;
        },
        getEDSData(edsData) {
            this.signData = edsData;
        },
        clearForm() {
            if (this.$refs.StylusSign) {
                this.$refs.StylusSign.$refs.signaturePad.clearSignature();
            }
            this.signData = null;
            this.selectedSigner = null;
            this.signers = [];
        },
        async getData() {
            this.isLoading = true;
            const response = await this.getActSignInfo(this.transferData.record_id);
            this.isLoading = false;
            this.signers = translate(response.data.signers, 'full', this.$i18n.locale) ?? [];
            this.base64document = response.data['document_to_sign'];
            this.pdfUrl = base64ToObjectURL(response.data['document_to_sign']);
            const signsAvailable = response.data['sign_types'];
            for (const key in signsAvailable) {
                switch (key) {
                    case 'stylus':
                        this.signMethods[0].show = signsAvailable[key];
                        if (signsAvailable[key]) {
                            this.selectedSignMethod = SignTypes.STYLUS;
                        }
                        break;
                    case 'eds':
                        this.signMethods[1].show = signsAvailable[key];
                        if (signsAvailable[key]) {
                            this.selectedSignMethod = SignTypes.EDS;
                        }
                        break;
                    case 'qr':
                        this.signMethods[2].show = signsAvailable[key];
                        if (signsAvailable[key]) {
                            this.selectedSignMethod = SignTypes.QRCode;
                        }

                        break;
                }
            }
        },
        async save() {
            if (!this.$refs.form.validate()) return;
            let payload = {};
            payload.record_id = this.transferData.record_id;
            payload.is_stylus = this.selectedSignMethod === SignTypes.STYLUS;
            if (this.selectedSignMethod === SignTypes.STYLUS) {
                this.getStylusData();
            }
            payload[this.signatory] = this.signData;
            await this.postActSign(payload);
            if (this.signers.length === 1) {
                this.cancel();
            } else {
                this.clearForm();
                await this.getData();
            }
        }
    },
    mounted() {
        this.getData(this.transferData);
    }
};
</script>

<style scoped>
#main {
    display: inline-block;
    width: 100%;
    height: 100%;
    max-height: 100%;
    vertical-align: top;
    overflow: hidden;
}
#inner {
    height: 400px;
    max-height: 400px;
    overflow-y: scroll;
    background: ivory;
}
</style>
